import { FC } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { type PageSize } from "../../components/ConfigListShell/useConfigList";

/**
 * SIMS-8783 — the list footer: rows-per-page and page navigation.
 *
 * It sits in the FOOTER of each list, not beside the view toggle. `export/README.md` claims the
 * opposite, but it is stale on this point — the markup renders this block inside each list
 * container (lines 292, 372, 428, 489), four identical copies. Second-reader diff D8.
 *
 * `page` is 0-BASED — `useConfigList` starts at `useState(0)` and computes `start = page * size`.
 * Treating it as 1-based traps the user on page 2 (Previous disabled) and leaves Next live but
 * inert on the last page. The worked example is src/pages/Payers/index.tsx:395,404.
 *
 * The design disables prev/next with opacity alone (`prevOpacity`/`nextOpacity`), leaving them
 * focusable and clickable. That is an accessibility defect, so this uses a real `disabled`
 * attribute — the same file already demonstrates the correct pattern on the editor's Save button.
 */
export interface ListFooterProps {
	/** 0-based, as `useConfigList` reports it. */
	page: number;
	pageCount: number;
	onPageChange: (page: number) => void;
	pageSize: PageSize;
	onPageSizeChange: (size: PageSize) => void;
	pageSizeOptions: readonly string[];
	rangeStart: number;
	rangeEnd: number;
	total: number;
}

const ListFooter: FC<ListFooterProps> = ({
	page,
	pageCount,
	onPageChange,
	pageSize,
	onPageSizeChange,
	pageSizeOptions,
	rangeStart,
	rangeEnd,
	total,
}) => (
	<Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 1.25 }}>
		<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
			<Box
				component="label"
				htmlFor="config-rows-per-page"
				sx={{ fontSize: "0.75rem", color: "text.secondary" }}
			>
				Rows per page
			</Box>
			<Box
				component="select"
				id="config-rows-per-page"
				value={pageSize}
				onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
					onPageSizeChange(e.target.value)
				}
				sx={{
					height: "2rem",
					border: "1px solid",
					borderColor: "divider",
					borderRadius: "0.5rem",
					padding: "0 0.5rem",
					fontSize: "0.8125rem",
				}}
			>
				{pageSizeOptions.map((o) => (
					<option key={o} value={o}>
						{o === "all" ? "All" : o}
					</option>
				))}
			</Box>
		</Box>
		<Box sx={{ flex: 1 }} />
		<Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
			{`${rangeStart}–${rangeEnd} of ${total}`}
		</Typography>
		<IconButton
			aria-label="Previous page"
			disabled={page === 0}
			onClick={() => onPageChange(page - 1)}
		>
			<ChevronLeftIcon fontSize="small" />
		</IconButton>
		<IconButton
			aria-label="Next page"
			disabled={page >= pageCount - 1}
			onClick={() => onPageChange(page + 1)}
		>
			<ChevronRightIcon fontSize="small" />
		</IconButton>
	</Box>
);

export default ListFooter;
